import { Item, LinkedItem, LinkedItemSon, SceneObjects } from '@types'
import { isHoodItem } from '@utils/kitchenInspector/classifyItem'
import isItem from '@utils/sceneObjects/assertions/isItem'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import isLinkedSon from '@utils/sceneObjects/assertions/isLinkedSon'
import isHiddenWall from '@utils/sceneObjects/assertions/isHiddenWall'
import isPartition from '@utils/sceneObjects/assertions/isPartition'
import isWall from '@utils/sceneObjects/assertions/isWall'
import { getWallCOBB } from '@utils/sceneObjects/walls/getWallCOBB'
import { GRID_SIZE } from '@constants'
import { sceneGLBLookupKey } from '@context/utils/linked/buildLinkedCatalogId'
import { useAppStore } from '@store'
import { getCatalogGlbAabbMm } from '@utils/sceneObjects/items/getGLBSize'
import {
  getEncimeraAcabadoThicknessMm,
  getEncimeraSitTopOffsetMm,
  getEncimeraTextureEntry,
  getEncimeraTypeThicknessMm,
  getIndependentZocaloChildYLiftMm,
  getLinkedApplianceLocalYMm,
  getLinkedPlacaCenteredLocalXMm,
  getLinkedPlacaSitLocalYMm,
  getFreeDimensionEncimeraLocalYMm,
  isEncimeraLinkedSonItem,
  isFregaderoLinkedSon,
  isFreeDimensionLateralOrCostadoCatalog,
  isGvMoldingUnit,
  isStandaloneZocaloTiraCatalog,
  isPlacaLinkedApplianceSon,
  usesFreeDimensionScaling,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import { getObbCenter } from '@utils/sceneObjects/snappedSides'
import { Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'

/** El sólido del mueble se recorta este tanto por arriba: sentarse en el tope no cuenta como penetrar. */
const SEAT_SINK_MM = 4
/** Holgura de contacto (mm): cara contra cara OK, interior no. */
const CONTACT_TOLERANCE_MM = 1
/** Grosor de encimera fuera de este rango se ignora. */
const MAX_ENCIMERA_THICKNESS_MM = 80
/** Por debajo de esto, un objeto suelto se dibuja encima de la unidad seleccionada. */
const MAX_SMALL_OBJECT_HEIGHT_MM = 550
/** Holgura sobre el cristal/parrilla o el borde del fregadero. */
const LINKED_SURFACE_PROUD_MM = 2
/** Alto de placa / labio de fregadero (no la cubeta). */
const MAX_LINKED_SURFACE_PROUD_MM = 80

const SCENE_BOUND_MARGIN_MM = 40

const lastValidById = new Map<
  string,
  {
    x: number
    y: number
    z: number
    quaternion: Quaternion
    hostId?: string
    startX: number
    startZ: number
    startHostId?: string
  }
>()
const probeOBB = new OBB()
const neighborOBB = new OBB()
const probeCore = new OBB()
const neighborCore = new OBB()

type ItemWithGlbPivot = {
  freeDimensionGlbMinYMm?: number
  glbSize?: { y?: number }
}

export type SmallCountertopAppliance = Item | LinkedItem

function normalizeApplianceCode(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/^PE\s+/, '')
    .replace(/[^A-Z0-9]/g, '')
}

/** Micros de encimera del menú de pequeños (MICROTPC1, MICROTPC2, …). */
export function isCountertopMicroCatalog(item?: SceneObjects | null): boolean {
  if (!item) return false
  const fields = [
    'catalogID' in item ? item.catalogID : '',
    'codigo' in item ? (item as { codigo?: string | number }).codigo : '',
    'referencia' in item ? (item as { referencia?: string }).referencia : '',
    'description' in item ? item.description : '',
  ]
  return fields.some((field) => /^MICROTPC\d+$/.test(normalizeApplianceCode(field)))
}

function normalizeMenuClave(clave?: string | null): string {
  return String(clave ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
}

const DECORATION_SUBMENU_KEYS = new Set(['ILUMINACIONMENU', 'ESTANTERIASMENU', 'PLANTASMENU'])

function isDecorationMenuClave(clave?: string | null): boolean {
  const normalized = normalizeMenuClave(clave)
  if (!normalized) return false
  if (normalized === 'DECORACIONMENU' || normalized.startsWith('DECORACION')) return true
  return DECORATION_SUBMENU_KEYS.has(normalized)
}

/** Unidades de DECORACIÓNMENU (plantas, sofás, cuadros, estanterías, etc.). */
export function isDecorationMenuUnit(item?: SceneObjects | null): boolean {
  if (!item) return false
  const principal = 'menuPrincipal' in item ? (item as { menuPrincipal?: string }).menuPrincipal : ''
  const clave = 'claveMenu' in item ? (item as { claveMenu?: string }).claveMenu : ''
  return isDecorationMenuClave(principal) || isDecorationMenuClave(clave)
}

/**
 * Se dibujan encima de la unidad seleccionada.
 * Pequeños electrodomésticos, decoración de cocina, bandejas, jarrones, cestos,
 * candelabros, velas, y cualquier otro objeto suelto de poca altura.
 * El resto respeta la altura de colocación. Todos pueden pegarse a la pared.
 */
function isFreePlacementClave(clave?: string | null): boolean {
  const normalized = normalizeMenuClave(clave)
  if (!normalized || normalized === 'DECORACIONMENU') return false
  if (normalized.includes('PEQUENOSELECTRO') || normalized.includes('ELECTRODOMESTICOSPEQUENO')) {
    return true
  }
  if (normalized.includes('DECORACIONDECOCINA') || normalized.includes('DECORACIONCOCINA')) return true
  if (normalized.includes('BANDEJA')) return true
  if (normalized.includes('JARRON')) return true
  if (normalized.includes('CESTO') || normalized.includes('CESTA')) return true
  if (normalized.includes('CANDELABRO')) return true
  return normalized.includes('VELA')
}

export function isFreePlacementDecoration(item?: SceneObjects | null): boolean {
  if (!item) return false
  const clave = 'claveMenu' in item ? (item as { claveMenu?: string }).claveMenu : ''
  const principal = 'menuPrincipal' in item ? (item as { menuPrincipal?: string }).menuPrincipal : ''
  if (isFreePlacementClave(clave) || isFreePlacementClave(principal)) return true
  const catalog = 'catalogID' in item ? item.catalogID : ''
  const codigo = 'codigo' in item ? (item as { codigo?: string | number }).codigo : ''
  const description = 'description' in item ? item.description : ''
  return isFreePlacementClave(`${catalog ?? ''} ${codigo ?? ''} ${description ?? ''}`)
}

function isKitchenRunTipo(item: SceneObjects): boolean {
  const tipo = String('tipoNormalizado' in item ? item.tipoNormalizado : '').toUpperCase()
  return tipo === 'MBJ' || tipo === 'MAL' || tipo === 'COL'
}

/** Decoración o universal: no forma parte del tramo de muebles. */
function isLooseCatalogObject(item: SceneObjects): boolean {
  if (isDecorationMenuUnit(item)) return true
  return 'universal' in item && Boolean((item as { universal?: boolean }).universal)
}

function isExcludedFromLoosePlacement(item: SceneObjects): boolean {
  if (isHoodItem(item)) return true
  if (isKitchenRunTipo(item)) return true
  if (!isItem(item) && !isLinkedItem(item)) return false
  if (isGvMoldingUnit(item) || isFreeDimensionLateralOrCostadoCatalog(item)) return true
  if (isStandaloneZocaloTiraCatalog(item)) return true
  return false
}

/**
 * Pequeños electrodomésticos, la decoración de encimera y el resto de objetos
 * sueltos de poca altura: se dibujan encima de la unidad seleccionada.
 */
export function isSmallCountertopAppliance(
  item?: SceneObjects | null,
): item is SmallCountertopAppliance {
  if (!item || (!isItem(item) && !isLinkedItem(item))) return false
  if (isHoodItem(item)) return false
  if (isCountertopMicroCatalog(item) || isFreePlacementDecoration(item)) return true
  if (isExcludedFromLoosePlacement(item) || !isLooseCatalogObject(item)) return false
  const height = item.height || 0
  return height > 0 && height <= MAX_SMALL_OBJECT_HEIGHT_MM
}

/**
 * Sofás, cuadros, estanterías y demás objetos sueltos que no son pequeños.
 * Conservan la altura de colocación y chocan por volumen, sin pegarse al lateral
 * del mueble ni quedarse dentro.
 */
export function usesFixedHeightVolumeCollision(item?: SceneObjects | null): boolean {
  if (!item || (!isItem(item) && !isLinkedItem(item))) return false
  if (isSmallCountertopAppliance(item) || isExcludedFromLoosePlacement(item)) return false
  if (isLooseCatalogObject(item)) return true
  // Sin colisión de tramo el arrastre 3D los atraviesa en cuanto la altura
  // no coincide con la del mueble.
  return 'canCollide' in item && item.canCollide === false
}

/** Grosor mínimo de choque: un cuadro casi plano sigue ocupando sitio delante de la pared. */
const MIN_COLLISION_DEPTH_MM = 40

function isVolumeObstacle(candidate: SceneObjects): candidate is Item | LinkedItem {
  if (isLinkedSon(candidate)) return false
  if (!isItem(candidate) && !isLinkedItem(candidate)) return false
  if (candidate.visible === false) return false
  if (isGvMoldingUnit(candidate) || isFreeDimensionLateralOrCostadoCatalog(candidate)) return false
  if (isStandaloneZocaloTiraCatalog(candidate)) return false
  const width = candidate.width || 0
  const height = candidate.height || 0
  return width >= 40 && height >= 40
}

function collisionDepthMm(thickness: number): number {
  return Math.max(thickness || 0, MIN_COLLISION_DEPTH_MM)
}

/** Mueble que puede hacer de apoyo / obstáculo sólido. */
export function isFurnitureHostUnit(item?: SceneObjects | null): item is LinkedItem {
  if (!item || !isLinkedItem(item) || item.visible === false) return false
  if (isSmallCountertopAppliance(item)) return false
  if (isGvMoldingUnit(item) || isFreeDimensionLateralOrCostadoCatalog(item)) return false
  const host = item as LinkedItem
  const width = host.width || 0
  const thickness = host.thickness || 0
  return width >= 80 && thickness >= 80 && (host.height || 0) > 0
}

function findEncimeraChild(
  unit: LinkedItem,
  items: Map<string, SceneObjects>,
): LinkedItemSon | undefined {
  if (!unit.childs) return undefined
  for (const childId of unit.childs) {
    const child = items.get(childId)
    if (isLinkedSon(child) && isEncimeraLinkedSonItem(child)) return child
  }
  return undefined
}

function getEncimeraThicknessMm(encimera: LinkedItemSon): number {
  const fromType = getEncimeraTypeThicknessMm(encimera as never)
  if (fromType != null) return fromType
  const fromStore = Number(encimera.height)
  if (Number.isFinite(fromStore) && fromStore > 0 && fromStore <= MAX_ENCIMERA_THICKNESS_MM) {
    return fromStore
  }
  const fromAcabado = getEncimeraAcabadoThicknessMm(getEncimeraTextureEntry(encimera)?.acabado)
  return fromAcabado ?? 0
}

function isPlacaOrFregaderoSon(child?: SceneObjects | null): child is LinkedItemSon {
  if (!child || !isLinkedSon(child)) return false
  return isPlacaLinkedApplianceSon(child) || isFregaderoLinkedSon(child)
}

function getSonGlbAabb(son: LinkedItemSon) {
  try {
    const sceneGLB = useAppStore.getState().sceneGLB
    return getCatalogGlbAabbMm(sceneGLB as never, son.catalogID, [
      sceneGLBLookupKey(String(son.catalogID ?? '')),
      (son as { idUnidades?: number }).idUnidades,
    ])
  } catch {
    return null
  }
}

function getLinkedSonLocalYMm(
  son: LinkedItemSon,
  parent: LinkedItem,
  items: Map<string, SceneObjects>,
): number {
  const encimera = findEncimeraChild(parent, items)
  if (isPlacaLinkedApplianceSon(son) && usesFreeDimensionScaling(parent)) {
    const placaAabb = getSonGlbAabb(son)
    const encimeraAabb = encimera ? getSonGlbAabb(encimera) : null
    const topOffset = encimera
      ? getEncimeraSitTopOffsetMm(encimera as never, encimeraAabb)
      : 10
    return getLinkedPlacaSitLocalYMm(
      getFreeDimensionEncimeraLocalYMm(parent),
      topOffset,
      placaAabb?.minY ?? 0,
    )
  }
  const stored = Number(son.y)
  if (!usesFreeDimensionScaling(parent)) {
    return Number.isFinite(stored) ? stored : 0
  }
  const encimeraAabb = encimera ? getSonGlbAabb(encimera) : null
  const computed = getLinkedApplianceLocalYMm(son as never, parent as never, encimera as never, {
    encimeraAabb,
    glbMinYMm: getSonGlbAabb(son)?.minY,
  })
  if (!Number.isFinite(stored)) return computed
  if (Math.abs(stored - computed) < 80) return stored
  return computed
}

/** Huella XZ visual (placa: AABB del GLB centrado, no el hueco API). */
function getLinkedSonFootprintLocalMm(
  son: LinkedItemSon,
  parent: LinkedItem,
): { x: number; z: number; width: number; thickness: number } {
  const aabb = getSonGlbAabb(son)
  let x = son.x ?? 0
  let z = son.z ?? 0
  let width = son.width || 0
  let thickness = son.thickness || 0
  if (isPlacaLinkedApplianceSon(son) && aabb) {
    const plateW = Math.max(8, aabb.maxX - aabb.minX)
    const plateD = Math.max(8, Math.abs(aabb.maxZ - aabb.minZ))
    const sitX =
      getLinkedPlacaCenteredLocalXMm(parent.width ?? 0, plateW, aabb.minX) ?? (son.x ?? 0)
    x = sitX + aabb.minX
    z = (son.z ?? 0) + aabb.minZ
    width = plateW
    thickness = plateD
  }
  const maxW = (parent.width || width) + 40
  const maxD = (parent.thickness || thickness) + 40
  if (width > maxW) width = Math.max(8, son.width || maxW)
  if (thickness > maxD) thickness = Math.max(8, son.thickness || maxD)
  return { x, z, width, thickness }
}

/** Alto por encima del origen del GLB (placa: parrillas; fregadero: labio, no cubeta). */
function getLinkedSonProudHeightMm(
  son: LinkedItemSon,
  localY: number,
  encimeraLocalY: number,
): number {
  const aabb = getSonGlbAabb(son)
  const glbMm =
    Number((son as { glbSize?: { y?: number } }).glbSize?.y) > 0
      ? Math.round(Number((son as { glbSize?: { y?: number } }).glbSize?.y) * 1000)
      : 0
  const heightMm = Number(son.height) || 0
  const aabbMaxY = aabb && Number.isFinite(aabb.maxY) && aabb.maxY > 0 ? aabb.maxY : 0

  if (isPlacaLinkedApplianceSon(son)) {
    // AABB del GLB actual: no mezclar con glbSize del modelo anterior tras un swap.
    if (aabbMaxY > 0) return aabbMaxY
    if (glbMm > 0) return glbMm
    return Math.max(heightMm, 1)
  }

  if (aabbMaxY > 0 && aabbMaxY <= MAX_LINKED_SURFACE_PROUD_MM) return aabbMaxY
  const proud = Math.max(glbMm, heightMm)
  if (proud > 0 && proud <= MAX_LINKED_SURFACE_PROUD_MM) return proud
  if (Math.abs(localY - encimeraLocalY) <= 50) {
    return aabbMaxY > 0 ? Math.min(aabbMaxY, MAX_LINKED_SURFACE_PROUD_MM) : 12
  }
  return aabbMaxY > 0 ? aabbMaxY : Math.max(proud, 1)
}

function applianceCenterOverLinkedSon(
  appliancePos: Vector3,
  applianceQuat: Quaternion,
  appliance: SmallCountertopAppliance,
  son: LinkedItemSon,
  parent: LinkedItem,
): boolean {
  const footprint = getLinkedSonFootprintLocalMm(son, parent)
  if (!(footprint.width > 8) || !(footprint.thickness > 8)) return false
  const center = applianceCenter(appliancePos, applianceQuat, appliance)
  const local = worldToUnitLocal(center, parent)
  return (
    local.x >= footprint.x &&
    local.x <= footprint.x + footprint.width &&
    local.z >= footprint.z &&
    local.z <= footprint.z + footprint.thickness
  )
}

function getOverlappingLinkedSurfaceWorldTopYMm(
  appliance: SmallCountertopAppliance,
  appliancePos: Vector3,
  applianceQuat: Quaternion,
  unit: LinkedItem,
  items: Map<string, SceneObjects>,
): number | null {
  if (!unit.childs?.size) return null
  const parentPose = decomposeOrigin(unit.matrix)
  const encimeraLocalY = getFreeDimensionEncimeraLocalYMm(unit)
  let best: number | null = null
  for (const childId of unit.childs) {
    const son = items.get(childId)
    if (!isPlacaOrFregaderoSon(son)) continue
    if (!applianceCenterOverLinkedSon(appliancePos, applianceQuat, appliance, son, unit)) continue
    const localY = getLinkedSonLocalYMm(son, unit, items)
    const proud = getLinkedSonProudHeightMm(son, localY, encimeraLocalY)
    const localTop = new Vector3(0, localY + proud + LINKED_SURFACE_PROUD_MM, 0)
    const worldTop = parentPose.position.y + localTop.applyQuaternion(parentPose.quaternion).y
    best = best == null ? worldTop : Math.max(best, worldTop)
  }
  return best
}

/**
 * Tope de apoyo (mm mundo).
 * En KBB el store.height es el cuerpo (800); el zócalo hijo levanta el mesh 100 mm
 * sin tocar item.y. Hay que sumar ese lift. Si ya se aplicó el offset al suelo
 * (item.y += 100), no se vuelve a sumar.
 */
export function getUnitSupportTopYMm(
  unit: LinkedItem,
  items: Map<string, SceneObjects>,
): number {
  const floorOffsetApplied = Boolean(
    (unit as { independentZocaloFloorOffsetApplied?: boolean }).independentZocaloFloorOffsetApplied,
  )
  const zocaloLift = floorOffsetApplied ? 0 : getIndependentZocaloChildYLiftMm(unit)
  const bodyTop = (unit.y ?? 0) + (unit.height || 0) + zocaloLift
  const encimera = findEncimeraChild(unit, items)
  if (!encimera) return bodyTop
  return bodyTop + getEncimeraThicknessMm(encimera)
}

export function getApplianceVisualBaseOffsetYMm(appliance: SmallCountertopAppliance): number {
  const minYMm = Number((appliance as ItemWithGlbPivot).freeDimensionGlbMinYMm)
  if (!Number.isFinite(minYMm) || minYMm === 0) return 0
  const glbHeightM = Number((appliance as ItemWithGlbPivot).glbSize?.y)
  const glbHeightMm = Number.isFinite(glbHeightM) && glbHeightM > 0 ? glbHeightM * 1000 : 0
  const scaleY = glbHeightMm > 0 && appliance.height > 0 ? appliance.height / glbHeightMm : 1
  return minYMm * scaleY
}

export function getSeatedApplianceYMm(
  appliance: SmallCountertopAppliance,
  unit: LinkedItem,
  items: Map<string, SceneObjects>,
  pose?: { position: Vector3; quaternion: Quaternion },
): number {
  const encimeraTop = getUnitSupportTopYMm(unit, items)
  const appliancePose = pose ?? decomposeOrigin(appliance.matrix)
  const overlayTop = getOverlappingLinkedSurfaceWorldTopYMm(
    appliance,
    appliancePose.position,
    appliancePose.quaternion,
    unit,
    items,
  )
  const supportTop = overlayTop != null ? Math.max(encimeraTop, overlayTop) : encimeraTop
  return supportTop - getApplianceVisualBaseOffsetYMm(appliance)
}

/** Y de apoyo en el suelo (mm), compensando el pivote visual del GLB. */
export function getFloorApplianceYMm(appliance: SmallCountertopAppliance): number {
  return 0 - getApplianceVisualBaseOffsetYMm(appliance)
}

/** Mueble con encimera (o bajo MBJ): tope al que se pegan pequeños y decoración. */
export function isEncimeraHostUnit(
  item: SceneObjects | null | undefined,
  items: Map<string, SceneObjects>,
): item is LinkedItem {
  if (!isFurnitureHostUnit(item)) return false
  if (findEncimeraChild(item, items)) return true
  return String(item.tipoNormalizado || '').toUpperCase() === 'MBJ'
}

function decomposeOrigin(matrix: { decompose: (p: Vector3, q: Quaternion, s: Vector3) => void }) {
  const position = new Vector3()
  const quaternion = new Quaternion()
  matrix.decompose(position, quaternion, new Vector3())
  return { position, quaternion }
}

function fillOBB(
  origin: Vector3,
  quaternion: Quaternion,
  width: number,
  height: number,
  thickness: number,
  target: OBB,
): OBB {
  target.center.copy(getObbCenter(origin, quaternion, width, height, thickness))
  target.halfSize.set(width / 2, height / 2, thickness / 2)
  target.rotation.setFromMatrix4(new Matrix4().makeRotationFromQuaternion(quaternion))
  return target
}

function shrinkUniform(source: OBB, tolMm: number, target: OBB): OBB {
  target.center.copy(source.center)
  target.rotation.copy(source.rotation)
  const t = Math.max(0, tolMm)
  target.halfSize.set(
    Math.max(1, source.halfSize.x - t),
    Math.max(1, source.halfSize.y - t),
    Math.max(1, source.halfSize.z - t),
  )
  return target
}

function footprintsOverlapXZ(
  appliancePos: Vector3,
  applianceQuat: Quaternion,
  appliance: SmallCountertopAppliance,
  unit: LinkedItem,
): boolean {
  fillOBB(
    appliancePos,
    applianceQuat,
    appliance.width,
    appliance.height || 0,
    appliance.thickness || 0,
    probeOBB,
  )
  probeOBB.halfSize.y = 1e6
  const unitPose = decomposeOrigin(unit.matrix)
  fillOBB(
    unitPose.position,
    unitPose.quaternion,
    unit.width,
    unit.height || 0,
    unit.thickness || 0,
    neighborOBB,
  )
  neighborOBB.halfSize.y = 1e6
  return probeOBB.intersectsOBB(neighborOBB)
}

function findEncimeraHostAtPose(
  appliancePos: Vector3,
  applianceQuat: Quaternion,
  appliance: SmallCountertopAppliance,
  items: Map<string, SceneObjects>,
): LinkedItem | undefined {
  let best: LinkedItem | undefined
  let bestDist = Infinity
  items.forEach((candidate) => {
    if (!isEncimeraHostUnit(candidate, items)) return
    if (!footprintsOverlapXZ(appliancePos, applianceQuat, appliance, candidate)) return
    const dist = Math.abs(
      (appliancePos.y ?? 0) -
        getSeatedApplianceYMm(appliance, candidate, items, {
          position: appliancePos,
          quaternion: applianceQuat,
        }),
    )
    if (dist < bestDist) {
      bestDist = dist
      best = candidate
    }
  })
  return best
}

export function findHostUnitUnderAppliance(
  appliance: SmallCountertopAppliance,
  items: Map<string, SceneObjects>,
): LinkedItem | undefined {
  const pose = decomposeOrigin(appliance.matrix)
  return findEncimeraHostAtPose(pose.position, pose.quaternion, appliance, items)
}

/**
 * Tras cambiar placa/fregadero, los pequeños cuya huella XZ sigue sobre el
 * mueble y cuya Y está en la banda de encimera se vuelven a sentar (placa
 * nueva, o mármol si ya no pisan la placa).
 */
export function collectReseatPosesOnHost(
  host: LinkedItem,
  items: Map<string, SceneObjects>,
): Array<{ id: string; position: Vector3; quaternion: Quaternion }> {
  if (!isEncimeraHostUnit(host, items)) return []
  const encimeraTop = getUnitSupportTopYMm(host, items)
  const yMin = encimeraTop - 80
  const yMax = encimeraTop + MAX_LINKED_SURFACE_PROUD_MM + 80
  const poses: Array<{ id: string; position: Vector3; quaternion: Quaternion }> = []
  items.forEach((candidate) => {
    if (!isSmallCountertopAppliance(candidate)) return
    const pose = decomposeOrigin(candidate.matrix)
    if (!footprintsOverlapXZ(pose.position, pose.quaternion, candidate, host)) return
    const overlayTop = getOverlappingLinkedSurfaceWorldTopYMm(
      candidate,
      pose.position,
      pose.quaternion,
      host,
      items,
    )
    const visualY = pose.position.y + getApplianceVisualBaseOffsetYMm(candidate)
    const onPlaca = overlayTop != null && visualY > encimeraTop - 80
    const onMarble = visualY >= yMin && visualY <= yMax
    if (!onPlaca && !onMarble) return
    const seated = computeSeatedAppliancePose(candidate, host, items, { centerOnUnit: false })
    if (Math.abs(seated.position.y - pose.position.y) < 0.5) return
    poses.push({ id: candidate.id, position: seated.position, quaternion: seated.quaternion })
  })
  return poses
}

export function computeSeatedAppliancePose(
  appliance: SmallCountertopAppliance,
  unit: LinkedItem,
  items: Map<string, SceneObjects>,
  options?: { centerOnUnit?: boolean },
): { position: Vector3; quaternion: Quaternion } {
  const appliancePose = decomposeOrigin(appliance.matrix)
  const unitPose = decomposeOrigin(unit.matrix)

  if (!options?.centerOnUnit) {
    appliancePose.position.y = getSeatedApplianceYMm(appliance, unit, items, {
      position: appliancePose.position,
      quaternion: appliancePose.quaternion,
    })
    return appliancePose
  }

  const quaternion = unitPose.quaternion.clone()
  const unitMid = new Vector3(unit.width / 2, 0, (unit.thickness || 0) / 2).applyQuaternion(
    unitPose.quaternion,
  )
  const applianceHalf = new Vector3(
    appliance.width / 2,
    0,
    (appliance.thickness || 0) / 2,
  ).applyQuaternion(quaternion)
  const position = unitPose.position.clone().add(unitMid).sub(applianceHalf)
  position.y = getSeatedApplianceYMm(appliance, unit, items, { position, quaternion })
  return { position, quaternion }
}

function copyStoreSlabOBB(id: string, target: OBB): boolean {
  const boxes = useAppStore.getState().sceneBoundingBoxes
  const box = boxes.get(getWallCOBB(id))?.boundingBox ?? boxes.get(id)?.boundingBox
  if (!box || box.halfSize.x < 1 || box.halfSize.z < 0.5) return false
  target.copy(box)
  if (target.halfSize.y < 100) target.halfSize.y = 1500
  return true
}

/**
 * Losa de perímetro: start→end es la cara interior; el grosor va hacia atrás.
 * Misma geometría que projectItemOnWall.
 */
function fillPerimeterWallOBB(
  candidate: { start?: [number, number]; end?: [number, number]; thickness?: number; height?: number },
  target: OBB,
): boolean {
  const start = candidate.start
  const end = candidate.end
  const thickness = Number(candidate.thickness) || 0
  if (!start || !end || thickness <= 0) return false
  const dx = end[0] - start[0]
  const dz = end[1] - start[1]
  const length = Math.hypot(dx, dz)
  if (length < 1) return false
  const quaternion = new Quaternion().setFromUnitVectors(
    new Vector3(1, 0, 0),
    new Vector3(dx / length, 0, dz / length),
  )
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(quaternion)
  const height = Math.max(Number(candidate.height) || 0, 2500)
  target.center.set((start[0] + end[0]) / 2, height / 2, (start[1] + end[1]) / 2)
  target.center.sub(zAxis.multiplyScalar(thickness / 2))
  target.halfSize.set(length / 2, height / 2, thickness / 2)
  target.rotation.setFromMatrix4(new Matrix4().makeRotationFromQuaternion(quaternion))
  return true
}

function fillBlockingSlabOBB(candidate: SceneObjects, target: OBB): boolean {
  // Paredes: losa real (start→end interior, grosor hacia atrás). No usar el COBB
  // de snap, que entra ~grosor/2 en la habitación y ancla los objetos de encimera.
  if (isWall(candidate) || isHiddenWall(candidate)) {
    if (fillPerimeterWallOBB(candidate, target)) return true
    if (isHiddenWall(candidate) && candidate.matrix) {
      const pose = decomposeOrigin(candidate.matrix)
      const width = candidate.width || 0
      const height = Math.max(candidate.height || 0, 100)
      const thickness = Math.max(candidate.thickness || 0, 20)
      if (width > 1) {
        target.center.copy(pose.position)
        target.halfSize.set(width / 2, height / 2, thickness / 2)
        target.rotation.setFromMatrix4(new Matrix4().makeRotationFromQuaternion(pose.quaternion))
        return true
      }
    }
    return false
  }
  if (isPartition(candidate)) {
    if (copyStoreSlabOBB(candidate.id, target)) return true
    const width = candidate.width || 0
    const height = Math.max(candidate.height || 0, 100)
    const thickness = Math.max(candidate.thickness || 0, 20)
    const matrix = candidate.matrix
    if (matrix && width > 1) {
      const pose = decomposeOrigin(matrix)
      target.center.copy(pose.position)
      target.halfSize.set(width / 2, height / 2, thickness / 2)
      target.rotation.setFromMatrix4(new Matrix4().makeRotationFromQuaternion(pose.quaternion))
      return true
    }
    return fillPerimeterWallOBB(candidate, target)
  }
  return false
}

function getSceneXZBounds(items: Map<string, SceneObjects>): {
  minX: number
  maxX: number
  minZ: number
  maxZ: number
} {
  let minX = Infinity
  let maxX = -Infinity
  let minZ = Infinity
  let maxZ = -Infinity
  items.forEach((object) => {
    if (!isWall(object)) return
    for (const point of [object.start, object.end]) {
      if (!point || !Number.isFinite(point[0]) || !Number.isFinite(point[1])) continue
      minX = Math.min(minX, point[0])
      maxX = Math.max(maxX, point[0])
      minZ = Math.min(minZ, point[1])
      maxZ = Math.max(maxZ, point[1])
    }
  })
  if (!Number.isFinite(minX) || !Number.isFinite(minZ)) {
    const half = GRID_SIZE / 2
    return { minX: -half, maxX: half, minZ: -half, maxZ: half }
  }
  const inset =
    maxX - minX > SCENE_BOUND_MARGIN_MM * 4 && maxZ - minZ > SCENE_BOUND_MARGIN_MM * 4
      ? SCENE_BOUND_MARGIN_MM
      : 0
  return {
    minX: minX + inset,
    maxX: maxX - inset,
    minZ: minZ + inset,
    maxZ: maxZ - inset,
  }
}

function clampOriginToSceneXZ(
  origin: Vector3,
  quaternion: Quaternion,
  appliance: SmallCountertopAppliance,
  items: Map<string, SceneObjects>,
) {
  const bounds = getSceneXZBounds(items)
  const center = applianceCenter(origin, quaternion, appliance)
  const halfW = (appliance.width || 0) / 2
  const halfT = (appliance.thickness || 0) / 2
  const pad = Math.max(halfW, halfT)
  const minX = bounds.minX + pad
  const maxX = bounds.maxX - pad
  const minZ = bounds.minZ + pad
  const maxZ = bounds.maxZ - pad
  if (minX <= maxX) {
    const clampedX = Math.min(Math.max(center.x, minX), maxX)
    origin.x += clampedX - center.x
  }
  if (minZ <= maxZ) {
    const clampedZ = Math.min(Math.max(center.z, minZ), maxZ)
    origin.z += clampedZ - center.z
  }
}

type VolumeProbeOptions = { fullBody?: boolean; skipSlabs?: boolean; looseOnly?: boolean }

function findVolumePenetration(
  appliance: SmallCountertopAppliance,
  position: Vector3,
  quaternion: Quaternion,
  items: Map<string, SceneObjects>,
  ignoreHostIds?: Set<string>,
  options?: VolumeProbeOptions,
): boolean {
  fillOBB(
    position,
    quaternion,
    appliance.width,
    appliance.height || 0,
    options?.fullBody ? collisionDepthMm(appliance.thickness || 0) : appliance.thickness || 0,
    probeOBB,
  )
  shrinkUniform(probeOBB, CONTACT_TOLERANCE_MM, probeCore)

  for (const candidate of items.values()) {
    if (candidate.id === appliance.id) continue
    if (ignoreHostIds?.has(candidate.id)) continue
    if (options?.fullBody && isVolumeObstacle(candidate)) {
      if (
        options.looseOnly &&
        !isSmallCountertopAppliance(candidate) &&
        !usesFixedHeightVolumeCollision(candidate)
      ) {
        continue
      }
      const pose = decomposeOrigin(candidate.matrix)
      fillOBB(
        pose.position,
        pose.quaternion,
        candidate.width,
        Math.max(candidate.height || 0, 1),
        collisionDepthMm(candidate.thickness || 0),
        neighborOBB,
      )
    } else if (options?.fullBody) {
      if (isWall(candidate) || isPartition(candidate) || isHiddenWall(candidate)) {
        if (options.skipSlabs) continue
        if (candidate.visible === false) continue
        if (!fillBlockingSlabOBB(candidate, neighborOBB)) continue
      } else {
        continue
      }
    } else if (isFurnitureHostUnit(candidate)) {
      const pose = decomposeOrigin(candidate.matrix)
      const solidHeight = Math.max(
        1,
        getUnitSupportTopYMm(candidate, items) - (candidate.y ?? 0) - SEAT_SINK_MM,
      )
      fillOBB(
        pose.position,
        pose.quaternion,
        candidate.width,
        solidHeight,
        candidate.thickness || 0,
        neighborOBB,
      )
    } else if (isSmallCountertopAppliance(candidate) || usesFixedHeightVolumeCollision(candidate)) {
      const pose = decomposeOrigin(candidate.matrix)
      fillOBB(
        pose.position,
        pose.quaternion,
        candidate.width,
        candidate.height || 0,
        candidate.thickness || 0,
        neighborOBB,
      )
    } else if (isWall(candidate) || isPartition(candidate) || isHiddenWall(candidate)) {
      if (options?.skipSlabs) continue
      if (candidate.visible === false) continue
      if (!fillBlockingSlabOBB(candidate, neighborOBB)) continue
    } else {
      continue
    }
    shrinkUniform(neighborOBB, CONTACT_TOLERANCE_MM, neighborCore)
    if (probeCore.intersectsOBB(neighborCore)) return true
  }
  return false
}

export function doesPosePenetrateSolids(
  appliance: SmallCountertopAppliance,
  position: Vector3,
  quaternion: Quaternion,
  items: Map<string, SceneObjects>,
): boolean {
  return findVolumePenetration(appliance, position, quaternion, items)
}

/** La pose actual entra en otro mueble u objeto. Las paredes no cuentan: el snap ya está a tope. */
export function doesUnitOverlapSolids(
  item: SmallCountertopAppliance,
  items: Map<string, SceneObjects>,
  fullBody = false,
): boolean {
  const pose = decomposeOrigin(item.matrix)
  const ignore = fullBody
    ? undefined
    : overlappingEncimeraHostIds(item, pose.position, pose.quaternion, items)
  return findVolumePenetration(
    item,
    pose.position,
    pose.quaternion,
    items,
    ignore && ignore.size > 0 ? ignore : undefined,
    { fullBody, skipSlabs: true },
  )
}

export function slabSnapOverlapsUnits(itemId: string, items: Map<string, SceneObjects>): boolean {
  const item = items.get(itemId)
  if (!item || (!isItem(item) && !isLinkedItem(item))) return false
  return doesUnitOverlapSolids(item, items, usesFixedHeightVolumeCollision(item))
}


/** Si `proposed` penetra, busca un XZ cercano libre (centro de escena / hueco). */
export function findNonPenetratingPosition(
  appliance: SmallCountertopAppliance,
  items: Map<string, SceneObjects>,
  proposed: Vector3,
  quaternion: Quaternion,
  options?: VolumeProbeOptions,
): Vector3 {
  const hits = (pos: Vector3) =>
    findVolumePenetration(appliance, pos, quaternion, items, undefined, options)
  if (!hits(proposed)) return proposed.clone()
  const radii = [200, 400, 700, 1100, 1600, 2200, 3000]
  const steps = 8
  for (const radius of radii) {
    for (let i = 0; i < steps; i++) {
      const angle = (i / steps) * Math.PI * 2
      const sample = proposed.clone()
      sample.x += Math.cos(angle) * radius
      sample.z += Math.sin(angle) * radius
      if (!hits(sample)) return sample
    }
  }
  return proposed.clone()
}

function isSeatedOnUnit(
  appliance: SmallCountertopAppliance,
  position: Vector3,
  quaternion: Quaternion,
  unit: LinkedItem,
  items: Map<string, SceneObjects>,
): boolean {
  if (!footprintsOverlapXZ(position, quaternion, appliance, unit)) return false
  const encimeraTop = getUnitSupportTopYMm(unit, items)
  const visualY = position.y + getApplianceVisualBaseOffsetYMm(appliance)
  return visualY >= encimeraTop - 25 && visualY <= encimeraTop + MAX_LINKED_SURFACE_PROUD_MM + 25
}

function applianceCenter(
  origin: Vector3,
  quaternion: Quaternion,
  appliance: SmallCountertopAppliance,
): Vector3 {
  return getObbCenter(
    origin,
    quaternion,
    appliance.width,
    appliance.height || 0,
    appliance.thickness || 0,
  )
}

function worldToUnitLocal(worldPoint: Vector3, unit: LinkedItem): Vector3 {
  const pose = decomposeOrigin(unit.matrix)
  return worldPoint.clone().sub(pose.position).applyQuaternion(pose.quaternion.clone().invert())
}

/** XZ libre; Y = placa, fregadero, encimera o suelo. */
function resolveDragPose(
  appliance: SmallCountertopAppliance,
  position: Vector3,
  quaternion: Quaternion,
  items: Map<string, SceneObjects>,
): Vector3 {
  const next = position.clone()
  clampOriginToSceneXZ(next, quaternion, appliance, items)
  const host = findEncimeraHostAtPose(next, quaternion, appliance, items)
  next.y = host
    ? getSeatedApplianceYMm(appliance, host, items, { position: next, quaternion })
    : getFloorApplianceYMm(appliance)
  return next
}

function overlappingEncimeraHostIds(
  appliance: SmallCountertopAppliance,
  position: Vector3,
  quaternion: Quaternion,
  items: Map<string, SceneObjects>,
): Set<string> {
  const ids = new Set<string>()
  items.forEach((candidate) => {
    if (!isEncimeraHostUnit(candidate, items)) return
    if (!footprintsOverlapXZ(position, quaternion, appliance, candidate)) return
    ids.add(candidate.id)
  })
  return ids
}

function isFreePose(
  appliance: SmallCountertopAppliance,
  position: Vector3,
  quaternion: Quaternion,
  items: Map<string, SceneObjects>,
  options?: { preserveHeight?: boolean },
): Vector3 | null {
  if (options?.preserveHeight) {
    const next = position.clone()
    clampOriginToSceneXZ(next, quaternion, appliance, items)
    next.y = position.y
    if (findVolumePenetration(appliance, next, quaternion, items, undefined, { fullBody: true })) {
      return null
    }
    return next
  }
  const next = resolveDragPose(appliance, position, quaternion, items)
  const ignoreHostIds = overlappingEncimeraHostIds(appliance, next, quaternion, items)
  if (
    findVolumePenetration(
      appliance,
      next,
      quaternion,
      items,
      ignoreHostIds.size > 0 ? ignoreHostIds : undefined,
    )
  ) {
    return null
  }
  return next
}

function seatedHostId(
  appliance: SmallCountertopAppliance,
  position: Vector3,
  quaternion: Quaternion,
  items: Map<string, SceneObjects>,
): string | undefined {
  const host = findEncimeraHostAtPose(position, quaternion, appliance, items)
  if (!host || !isSeatedOnUnit(appliance, position, quaternion, host, items)) return undefined
  return host.id
}

/** Si un mueble de tramo ha quedado dentro de un cuadro, sofá u otro objeto suelto, lo saca. */
export function relocateOutOfLooseUnits(
  item: SmallCountertopAppliance,
  items: Map<string, SceneObjects>,
): { position: Vector3; quaternion: Quaternion } | null {
  const pose = decomposeOrigin(item.matrix)
  const probe: VolumeProbeOptions = { fullBody: true, skipSlabs: true, looseOnly: true }
  const hits = (pos: Vector3) =>
    findVolumePenetration(item, pos, pose.quaternion, items, undefined, probe)
  if (!hits(pose.position)) return null
  const free = findNonPenetratingPosition(item, items, pose.position, pose.quaternion, probe)
  if (hits(free)) return null
  return { position: free, quaternion: pose.quaternion }
}

export function lastFreeVolumePose(itemId: string): {
  position: Vector3
  quaternion: Quaternion
} | null {
  const stored = lastValidById.get(itemId)
  if (!stored) return null
  return {
    position: new Vector3(stored.x, stored.y, stored.z),
    quaternion: stored.quaternion.clone(),
  }
}

export function rememberVolumeDragPose(itemId: string, items: Map<string, SceneObjects>) {
  const item = items.get(itemId)
  if (!item || (!isItem(item) && !isLinkedItem(item))) return
  if (doesUnitOverlapSolids(item, items, true)) return
  beginSmallApplianceDrag(item, items)
}

export function beginSmallApplianceDrag(appliance: SmallCountertopAppliance, items: Map<string, SceneObjects>) {
  const pose = decomposeOrigin(appliance.matrix)
  const hostId = seatedHostId(appliance, pose.position, pose.quaternion, items)
  lastValidById.set(appliance.id, {
    x: pose.position.x,
    y: pose.position.y,
    z: pose.position.z,
    quaternion: pose.quaternion.clone(),
    hostId,
    startX: pose.position.x,
    startZ: pose.position.z,
    startHostId: hostId,
  })
}

export function ensureSmallApplianceDrag(appliance: SmallCountertopAppliance, items: Map<string, SceneObjects>) {
  if (lastValidById.has(appliance.id)) return
  beginSmallApplianceDrag(appliance, items)
}

export function endSmallApplianceDrag(applianceId: string) {
  lastValidById.delete(applianceId)
}

/**
 * Arrastre libre en XZ. La Y es placa, fregadero, encimera o suelo según
 * lo que haya debajo. No se ancla al tramo; paredes y límites de escena sí cortan.
 */
export function resolveSmallApplianceFreeDrag(
  appliance: SmallCountertopAppliance,
  items: Map<string, SceneObjects>,
  proposed: Vector3,
  options?: { preserveHeight?: boolean },
): { position: Vector3; quaternion: Quaternion } | null {
  const stored = lastValidById.get(appliance.id)
  const current = decomposeOrigin(appliance.matrix)
  const lastPos = stored
    ? new Vector3(stored.x, stored.y, stored.z)
    : current.position.clone()
  const quaternion = stored?.quaternion.clone() ?? current.quaternion.clone()
  const proposedXZ = proposed.clone()
  if (!options?.preserveHeight) proposedXZ.y = lastPos.y

  const tryAt = (pos: Vector3) => isFreePose(appliance, pos, quaternion, items, options)

  const commit = (pos: Vector3) => {
    lastValidById.set(appliance.id, {
      x: pos.x,
      y: pos.y,
      z: pos.z,
      quaternion: quaternion.clone(),
      hostId: seatedHostId(appliance, pos, quaternion, items),
      startX: stored?.startX ?? pos.x,
      startZ: stored?.startZ ?? pos.z,
      startHostId: stored?.startHostId,
    })
    return { position: pos, quaternion }
  }

  const direct = tryAt(proposedXZ)
  if (direct) return commit(direct)

  let lastFree = tryAt(lastPos)
  if (!lastFree) {
    if (options?.preserveHeight) {
      const escaped = findNonPenetratingPosition(appliance, items, proposedXZ, quaternion, {
        fullBody: true,
      })
      const escapedFree = tryAt(escaped)
      if (escapedFree) return commit(escapedFree)
      return null
    }
    lastFree = resolveDragPose(appliance, lastPos, quaternion, items)
  }

  let lo = 0
  let hi = 1
  let best = lastFree
  for (let i = 0; i < 16; i++) {
    const mid = (lo + hi) * 0.5
    const sample = new Vector3().lerpVectors(lastPos, proposedXZ, mid)
    if (!options?.preserveHeight) sample.y = lastPos.y
    const free = tryAt(sample)
    if (free) {
      lo = mid
      best = free
    } else {
      hi = mid
    }
  }

  const slideY = options?.preserveHeight ? proposedXZ.y : lastPos.y
  const axisTries = [
    new Vector3(proposedXZ.x, slideY, lastPos.z),
    new Vector3(lastPos.x, slideY, proposedXZ.z),
    new Vector3(proposedXZ.x, slideY, proposedXZ.z),
  ]
  let bestScore = best.distanceToSquared(proposedXZ)
  for (const tryPos of axisTries) {
    const free = tryAt(tryPos)
    if (!free) continue
    const score = free.distanceToSquared(proposedXZ)
    if (score < bestScore) {
      bestScore = score
      best = free
    }
  }

  return commit(best)
}

/** @deprecated Usar resolveSmallApplianceFreeDrag */
export function resolveSmallApplianceDrag(
  appliance: SmallCountertopAppliance,
  items: Map<string, SceneObjects>,
): { position: Vector3; quaternion: Quaternion; wrote: boolean } {
  const pose = decomposeOrigin(appliance.matrix)
  const resolved = resolveSmallApplianceFreeDrag(appliance, items, pose.position)
  if (!resolved) return { ...pose, wrote: false }
  const wrote =
    Math.abs(resolved.position.x - pose.position.x) > 0.5 ||
    Math.abs(resolved.position.y - pose.position.y) > 0.5 ||
    Math.abs(resolved.position.z - pose.position.z) > 0.5
  return { ...resolved, wrote }
}
